/**
 * Performance monitoring utility for tracking image loading and app performance
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      imageLoadTimes: [],
      pageLoadTimes: [],
      apiCallTimes: []
    };
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  /**
   * Track image loading time
   * @param {string} imageUrl - Image URL
   * @param {number} loadTime - Load time in milliseconds
   * @param {string} context - Context (e.g., 'feed', 'profile', 'post')
   */
  trackImageLoad(imageUrl, loadTime, context = 'unknown') {
    if (!this.isEnabled) return;

    const metric = {
      url: imageUrl,
      loadTime,
      context,
      timestamp: Date.now(),
      userAgent: navigator.userAgent
    };

    this.metrics.imageLoadTimes.push(metric);
    
    // Keep only last 100 entries
    if (this.metrics.imageLoadTimes.length > 100) {
      this.metrics.imageLoadTimes = this.metrics.imageLoadTimes.slice(-100);
    }

    console.log(`📊 Image loaded in ${loadTime}ms: ${imageUrl} (${context})`);
  }

  /**
   * Track page load time
   * @param {string} pageName - Page name
   * @param {number} loadTime - Load time in milliseconds
   */
  trackPageLoad(pageName, loadTime) {
    if (!this.isEnabled) return;

    const metric = {
      page: pageName,
      loadTime,
      timestamp: Date.now()
    };

    this.metrics.pageLoadTimes.push(metric);
    
    if (this.metrics.pageLoadTimes.length > 50) {
      this.metrics.pageLoadTimes = this.metrics.pageLoadTimes.slice(-50);
    }

    console.log(`📊 Page loaded in ${loadTime}ms: ${pageName}`);
  }

  /**
   * Track API call time
   * @param {string} endpoint - API endpoint
   * @param {number} responseTime - Response time in milliseconds
   * @param {boolean} success - Whether the call was successful
   */
  trackApiCall(endpoint, responseTime, success = true) {
    if (!this.isEnabled) return;

    const metric = {
      endpoint,
      responseTime,
      success,
      timestamp: Date.now()
    };

    this.metrics.apiCallTimes.push(metric);
    
    if (this.metrics.apiCallTimes.length > 100) {
      this.metrics.apiCallTimes = this.metrics.apiCallTimes.slice(-100);
    }

    const status = success ? '✅' : '❌';
    console.log(`${status} API call to ${endpoint} took ${responseTime}ms`);
  }

  /**
   * Get performance summary
   * @returns {Object} Performance summary
   */
  getPerformanceSummary() {
    const imageLoadTimes = this.metrics.imageLoadTimes.map(m => m.loadTime);
    const pageLoadTimes = this.metrics.pageLoadTimes.map(m => m.loadTime);
    const apiCallTimes = this.metrics.apiCallTimes.map(m => m.responseTime);

    return {
      imageLoad: {
        count: imageLoadTimes.length,
        average: imageLoadTimes.length > 0 ? imageLoadTimes.reduce((a, b) => a + b, 0) / imageLoadTimes.length : 0,
        min: imageLoadTimes.length > 0 ? Math.min(...imageLoadTimes) : 0,
        max: imageLoadTimes.length > 0 ? Math.max(...imageLoadTimes) : 0
      },
      pageLoad: {
        count: pageLoadTimes.length,
        average: pageLoadTimes.length > 0 ? pageLoadTimes.reduce((a, b) => a + b, 0) / pageLoadTimes.length : 0,
        min: pageLoadTimes.length > 0 ? Math.min(...pageLoadTimes) : 0,
        max: pageLoadTimes.length > 0 ? Math.max(...pageLoadTimes) : 0
      },
      apiCalls: {
        count: apiCallTimes.length,
        average: apiCallTimes.length > 0 ? apiCallTimes.reduce((a, b) => a + b, 0) / apiCallTimes.length : 0,
        min: apiCallTimes.length > 0 ? Math.min(...apiCallTimes) : 0,
        max: apiCallTimes.length > 0 ? Math.max(...apiCallTimes) : 0
      }
    };
  }

  /**
   * Log performance summary to console
   */
  logPerformanceSummary() {
    if (!this.isEnabled) return;

    const summary = this.getPerformanceSummary();
    
    console.group('📊 Performance Summary');
    console.log('Image Loading:', summary.imageLoad);
    console.log('Page Loading:', summary.pageLoad);
    console.log('API Calls:', summary.apiCalls);
    console.groupEnd();
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics = {
      imageLoadTimes: [],
      pageLoadTimes: [],
      apiCallTimes: []
    };
  }

  /**
   * Enable/disable monitoring
   * @param {boolean} enabled - Whether to enable monitoring
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;
